import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  BookOpen,
  FileUp,
  Film,
  Loader2,
  Mic2,
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Progress } from '@/components/ui/progress';
import { NarrationVoicePickerDialog } from '@/components/NarrationVoicePickerDialog';
import { TtsProviderStyleSelect } from '@/components/tts/TtsProviderStyleSelect';
import { StorybuilderHistoryPanel } from '@/components/storybuilder/StorybuilderHistoryPanel';
import { StorybuilderPageReviewPanel } from '@/components/storybuilder/StorybuilderPageReviewPanel';
import {
  flowHistoryPanelPaddingClass,
  readFlowHistoryPanelOpen,
  writeFlowHistoryPanelOpen,
} from '@/components/creationFlows/FlowHistoryPanel';
import api from '@/lib/api';
import { formatApiError } from '@/lib/formatApiError';
import { fetchMurfVoiceCatalog } from '@/lib/murfApi';
import { getIntakeNavPrefill } from '@/lib/intakeNavPrefill';
import { readAgentHandoff } from '@/lib/agentHandoff';
import { AgentHandoffGate } from '@/components/createPlan/AgentHandoffGate';
import {
  defaultVoiceStyleForProvider,
  normalizeVoiceStyleForProvider,
  shouldShowProviderStyleSelect,
} from '@/lib/ttsProviderStyles';

import { TwinneeLogo } from '@/components/TwinneeLogo';
const HISTORY_PANEL_KEY = 'storybuilder';
const providerLabel = {
  elevenlabs: 'ElevenLabs',
  murf: 'Murf',
  minimax: 'MiniMax',
};

async function uploadPdfToS3(file) {
  const filename = file.name || `storybuilder-${Date.now()}.pdf`;
  const contentType = file.type || 'application/pdf';
  const { data: presigned } = await api.post('/media/presigned-url', null, {
    params: { filename, content_type: contentType },
  });
  const uploadUrl = presigned.upload_url || presigned.presigned_url;
  const s3Key = presigned.s3_key;
  const viewUrl = presigned.view_url || presigned.object_url || presigned.s3_url;
  if (!uploadUrl || !s3Key) throw new Error('Could not get upload URL');
  const putRes = await fetch(uploadUrl, {
    method: 'PUT',
    body: file,
    headers: { 'Content-Type': contentType },
  });
  if (!putRes.ok) throw new Error(`S3 upload failed (${putRes.status})`);
  return { s3Key, viewUrl, filename, contentType };
}

/**
 * Storybuilder — PDF → per-page image + text → narration audio/video → editor.
 */
export function StoryBuilderFlowPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const fileRef = useRef(null);

  const [title, setTitle] = useState('');
  const [pdfFile, setPdfFile] = useState(null);
  const [language, setLanguage] = useState('en');
  const [voiceStyle, setVoiceStyle] = useState('storyteller');
  const [ttsProvider, setTtsProvider] = useState('auto');
  const [narratorElevenlabsVoiceId, setNarratorElevenlabsVoiceId] = useState('');
  const [narratorMurfVoiceId, setNarratorMurfVoiceId] = useState('');
  const [narratorMurfLocale, setNarratorMurfLocale] = useState('');
  const [narratorMinimaxVoiceId, setNarratorMinimaxVoiceId] = useState('');
  const [voicePickerOpen, setVoicePickerOpen] = useState(false);
  const [voiceProvider, setVoiceProvider] = useState('elevenlabs');
  const [murfVoices, setMurfVoices] = useState([]);

  const [loading, setLoading] = useState(false);
  const [continuing, setContinuing] = useState(false);
  const [projectId, setProjectId] = useState(null);
  const [job, setJob] = useState(null);
  const [historyOpen, setHistoryOpen] = useState(() => readFlowHistoryPanelOpen(HISTORY_PANEL_KEY, true));
  const [historyRefreshKey, setHistoryRefreshKey] = useState(0);
  const [loadingHistoryId, setLoadingHistoryId] = useState(null);

  const agentHandoff = useMemo(() => readAgentHandoff(location.state), [location.state]);
  const [handoffReady, setHandoffReady] = useState(false);

  useEffect(() => {
    if (!readAgentHandoff(location.state)) return;
    const nav = getIntakeNavPrefill(location.state);
    if (nav?.title) setTitle(nav.title);
    if (nav?.language) setLanguage(nav.language);
    setHandoffReady(true);
  }, [location.state]);

  const toggleHistory = useCallback(() => {
    setHistoryOpen((prev) => {
      const next = !prev;
      writeFlowHistoryPanelOpen(HISTORY_PANEL_KEY, next);
      return next;
    });
  }, []);

  const resetForNewProject = useCallback(() => {
    setTitle('');
    setPdfFile(null);
    setProjectId(null);
    setJob(null);
    setContinuing(false);
    if (fileRef.current) fileRef.current.value = '';
  }, []);

  const selectHistoryProject = useCallback(async (project) => {
    if (!project?.id) return;
    const st = String(project.status || '').toLowerCase();
    if (st === 'completed') {
      navigate(project.editor_path || `/narration/${project.id}/video-editor`);
      return;
    }
    setLoadingHistoryId(project.id);
    try {
      setProjectId(project.id);
      setTitle(project.title || '');
      const { data } = await api.get(`/storybuilder/projects/${project.id}/job`);
      setJob(data);
      if (data?.status === 'completed') {
        navigate(`/narration/${project.id}/video-editor`);
      } else if (data?.status === 'awaiting_review') {
        toast.info('Resume page review — crop & edit text, then continue');
      } else if (data?.status === 'failed') {
        toast.error(data.error_message || 'This Storybuilder run failed — you can retry below');
      } else {
        toast.info('Resumed Storybuilder progress');
      }
    } catch (err) {
      toast.error(formatApiError(err, 'Could not load project'));
    } finally {
      setLoadingHistoryId(null);
    }
  }, [navigate]);

  useEffect(() => {
    if (ttsProvider !== 'murf') return undefined;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await fetchMurfVoiceCatalog(api);
        if (!cancelled) setMurfVoices(data?.voices || []);
      } catch {
        if (!cancelled) setMurfVoices([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [ttsProvider]);

  useEffect(() => {
    if (
      !projectId
      || !job
      || job.status === 'completed'
      || job.status === 'failed'
      || job.status === 'awaiting_review'
    ) {
      return undefined;
    }
    let cancelled = false;
    const tick = async () => {
      try {
        const { data } = await api.get(`/storybuilder/projects/${projectId}/job`);
        if (!cancelled) setJob(data);
        if (!cancelled && data?.status === 'completed') {
          setHistoryRefreshKey((k) => k + 1);
          toast.success('Storybuilder ready');
          navigate(`/narration/${projectId}/video-editor`);
        }
        if (!cancelled && data?.status === 'awaiting_review') {
          setHistoryRefreshKey((k) => k + 1);
          toast.success('Pages extracted — review crops & text');
        }
      } catch {
        /* ignore poll errors */
      }
    };
    const id = setInterval(tick, 2500);
    tick();
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [projectId, job?.status, navigate]);

  const continueAfterReview = useCallback(async () => {
    if (!projectId) return;
    setContinuing(true);
    try {
      const { data } = await api.post(`/storybuilder/projects/${projectId}/continue`);
      setJob({
        id: data.job_id,
        status: data.status || 'pending',
        progress: 44,
        message: 'Continuing — generating audio',
      });
      setHistoryRefreshKey((k) => k + 1);
      toast.success('Building audio & video from your pages');
    } catch (err) {
      toast.error(formatApiError(err, 'Could not continue Storybuilder'));
    } finally {
      setContinuing(false);
    }
  }, [projectId]);
  const openVoicePicker = (provider = ttsProvider) => {
    if (!provider || provider === 'auto') {
      toast.info('Choose ElevenLabs, Murf, or MiniMax first');
      return;
    }
    setVoiceProvider(provider);
    setVoicePickerOpen(true);
  };

  const selectVoice = (voice) => {
    if (voiceProvider === 'elevenlabs') setNarratorElevenlabsVoiceId(voice.voice_id);
    if (voiceProvider === 'murf') {
      setNarratorMurfVoiceId(voice.voice_id);
      setNarratorMurfLocale(voice.locale || voice.effective_locale || narratorMurfLocale);
      setVoiceStyle((prev) =>
        normalizeVoiceStyleForProvider('murf', prev, {
          murfVoices,
          murfVoiceId: voice.voice_id,
          murfLocale: voice.locale || voice.effective_locale || narratorMurfLocale,
        }),
      );
    }
    if (voiceProvider === 'minimax') setNarratorMinimaxVoiceId(voice.voice_id);
    setVoicePickerOpen(false);
  };

  const onPickPdf = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const name = (file.name || '').toLowerCase();
    if (!name.endsWith('.pdf') && file.type !== 'application/pdf') {
      toast.error('Please upload a PDF');
      return;
    }
    setPdfFile(file);
    if (!title.trim()) {
      setTitle(file.name.replace(/\.pdf$/i, '') || 'Storybuilder project');
    }
  };

  const startBuilder = async () => {
    if (!pdfFile) {
      toast.error('Upload a PDF first');
      return;
    }
    if (!title.trim()) {
      toast.error('Add a title');
      return;
    }
    setLoading(true);
    setJob(null);
    try {
      const uploaded = await uploadPdfToS3(pdfFile);
      const payload = {
        title: title.trim(),
        pdf_s3_key: uploaded.s3Key,
        pdf_s3_url: uploaded.viewUrl || '',
        pdf_filename: uploaded.filename,
        language,
        voice_style: voiceStyle,
        tts_provider: ttsProvider,
        narrator_elevenlabs_voice_id: narratorElevenlabsVoiceId || null,
        narrator_murf_voice_id: narratorMurfVoiceId || null,
        narrator_murf_locale: narratorMurfLocale || null,
        narrator_minimax_voice_id: narratorMinimaxVoiceId || null,
        video_provider: 'narration_slideshow',
        image_aspect_ratio: '16:9',
      };
      const { data } = await api.post('/storybuilder/projects', payload);
      setProjectId(data.project?.id || data.project_id);
      setJob({
        id: data.job_id,
        status: data.status || 'pending',
        progress: 0,
        message: 'Queued — reading PDF',
      });
      setHistoryRefreshKey((k) => k + 1);
      toast.success('Storybuilder started');
    } catch (err) {
      toast.error(formatApiError(err, 'Failed to start Storybuilder'));
    } finally {
      setLoading(false);
    }
  };

  const progress = Number(job?.progress || 0);
  const awaitingReview = job?.status === 'awaiting_review';
  const jobRunning = job && (job.status === 'pending' || job.status === 'running');
  const showSetupForm = !awaitingReview && !jobRunning;
  const jobStale = useMemo(() => {
    if (!jobRunning || !job?.updated_at) return false;
    const updated = Date.parse(job.updated_at);
    if (!Number.isFinite(updated)) return false;
    return Date.now() - updated > 3 * 60 * 1000;
  }, [jobRunning, job?.updated_at]);
  const canOpenEditor = Boolean(
    projectId && (job?.status === 'completed' || progress >= 90 || jobStale),
  );
  const voiceHint = useMemo(() => {
    if (ttsProvider === 'elevenlabs' && narratorElevenlabsVoiceId) return 'ElevenLabs voice set';
    if (ttsProvider === 'murf' && narratorMurfVoiceId) return 'Murf voice set';
    if (ttsProvider === 'minimax' && narratorMinimaxVoiceId) return 'MiniMax voice set';
    if (ttsProvider === 'auto') return 'Auto voice';
    return 'Default voice';
  }, [
    ttsProvider,
    narratorElevenlabsVoiceId,
    narratorMurfVoiceId,
    narratorMinimaxVoiceId,
  ]);

  return (
    <div
      className={`relative min-h-screen bg-[#0b0e13] text-white font-jakarta pb-20 transition-[padding] duration-200 ease-out ${flowHistoryPanelPaddingClass(historyOpen)}`}
      data-testid="storybuilder-flow-page"
    >
      <AgentHandoffGate
        handoff={agentHandoff}
        ready={handoffReady}
        flowLabel="Storybuilder"
        busy={loading}
        onStart={startBuilder}
        fields={[
          {
            id: 'pdf',
            label: 'Story PDF',
            hint: 'Each page becomes a scene with its image and narration.',
            valid: Boolean(pdfFile),
            render: () => (
              <div className="flex items-center gap-3">
                <Input type="file" accept="application/pdf,.pdf" onChange={onPickPdf} className="cursor-pointer" />
                {pdfFile ? <span className="text-xs text-muted-foreground truncate max-w-[40%]">{pdfFile.name}</span> : null}
              </div>
            ),
          },
          {
            id: 'title',
            label: 'Project title',
            valid: Boolean(title.trim()),
            render: () => <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. The Brave Little Kite" />,
          },
        ]}
      />
      <div className="max-w-3xl mx-auto px-4 sm:px-6 pt-10 space-y-8">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          className="space-y-3"
        >
          <div className="inline-flex items-center gap-2 text-[#62FAE3] text-xs font-mono uppercase tracking-widest">
            <BookOpen className="w-3.5 h-3.5" />
            Storybuilder
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold tracking-tight">
            PDF → page videos in the editor
          </h1>
          <p className="text-sm text-[#9CA3AF] max-w-xl leading-relaxed">
            Upload a PDF. Each page becomes an image + text scene you can crop like narration —
            then we generate narration audio and slideshow video for the editor.
          </p>
        </motion.div>

        {!showSetupForm ? null : (
        <div className="rounded-2xl border border-[#1f2635] bg-[#12151d]/90 p-5 sm:p-6 space-y-5">
          <div className="space-y-2">
            <Label htmlFor="sb-title">Project title</Label>
            <Input
              id="sb-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="My storybook"
              className="bg-[#0b0e13] border-[#1f2635]"
              data-testid="storybuilder-title"
            />
          </div>

          <div className="space-y-2">
            <Label>PDF</Label>
            <input
              ref={fileRef}
              type="file"
              accept="application/pdf,.pdf"
              className="hidden"
              onChange={onPickPdf}
              data-testid="storybuilder-pdf-input"
            />
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              className="w-full rounded-xl border border-dashed border-[#2b3547] bg-[#0b0e13]/80 hover:border-[#62FAE3]/50 px-4 py-8 flex flex-col items-center gap-2 transition-colors cursor-pointer"
              data-testid="storybuilder-pdf-dropzone"
            >
              <FileUp className="w-7 h-7 text-[#62FAE3]" />
              <span className="text-sm font-medium">
                {pdfFile ? pdfFile.name : 'Choose a PDF'}
              </span>
              <span className="text-[11px] text-[#6B7280]">
                Each page → image + text → audio + video
              </span>
            </button>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Language</Label>
              <Select value={language} onValueChange={setLanguage}>
                <SelectTrigger className="bg-[#0b0e13] border-[#1f2635]" data-testid="storybuilder-language">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="en">English</SelectItem>
                  <SelectItem value="hi">Hindi</SelectItem>
                  <SelectItem value="es">Spanish</SelectItem>
                  <SelectItem value="fr">French</SelectItem>
                  <SelectItem value="de">German</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>TTS provider</Label>
              <Select
                value={ttsProvider}
                onValueChange={(v) => {
                  setTtsProvider(v);
                  setVoiceStyle(defaultVoiceStyleForProvider(v, voiceStyle));
                }}
              >
                <SelectTrigger className="bg-[#0b0e13] border-[#1f2635]" data-testid="storybuilder-tts">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="auto">Auto</SelectItem>
                  <SelectItem value="elevenlabs">ElevenLabs</SelectItem>
                  <SelectItem value="murf">Murf</SelectItem>
                  <SelectItem value="minimax">MiniMax</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {shouldShowProviderStyleSelect(ttsProvider) ? (
            <TtsProviderStyleSelect
              provider={ttsProvider}
              value={voiceStyle}
              onChange={setVoiceStyle}
              murfVoices={murfVoices}
              murfVoiceId={narratorMurfVoiceId}
              murfLocale={narratorMurfLocale}
            />
          ) : null}

          <div className="flex flex-wrap items-center gap-2">
            <Button
              type="button"
              variant="outline"
              className="border-[#1f2635] bg-white/5"
              onClick={() => openVoicePicker(ttsProvider)}
              data-testid="storybuilder-voice-btn"
            >
              <Mic2 className="w-4 h-4 mr-2" />
              Choose voice
            </Button>
            <span className="text-xs text-[#9CA3AF]">{voiceHint}</span>
          </div>

          <Button
            type="button"
            disabled={loading || Boolean(job && job.status !== 'failed' && job.status !== 'completed')}
            onClick={startBuilder}
            className="w-full h-11 rounded-xl bg-[#62FAE3] hover:bg-[#4de1c1] text-black font-bold"
            data-testid="storybuilder-start-btn"
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Uploading…
              </>
            ) : (
              <>
                <TwinneeLogo className="w-4 h-4 mr-2" /> Build story from PDF
              </>
            )}
          </Button>
        </div>
        )}

        {awaitingReview && projectId ? (
          <StorybuilderPageReviewPanel
            projectId={projectId}
            onContinue={continueAfterReview}
            continuing={continuing}
          />
        ) : null}

        {job && !awaitingReview ? (
          <div
            className="rounded-2xl border border-[#1f2635] bg-[#12151d] p-5 space-y-3"
            data-testid="storybuilder-progress"
          >
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-2 text-sm font-medium">
                {job.status === 'completed' ? (
                  <Film className="w-4 h-4 text-[#62FAE3]" />
                ) : job.status === 'failed' ? (
                  <span className="text-red-400">Failed</span>
                ) : (
                  <Loader2 className="w-4 h-4 animate-spin text-[#62FAE3]" />
                )}
                <span>{job.message || job.status}</span>
              </div>
              <span className="text-xs font-mono text-[#9CA3AF]">{progress}%</span>
            </div>
            <Progress value={progress} className="h-2" />
            {job.error_message ? (
              <p className="text-xs text-red-400">{job.error_message}</p>
            ) : null}
            {jobStale ? (
              <p className="text-xs text-amber-400/90">
                This run looks stuck (no progress for a few minutes — often after an API reload).
                Retry to finish remaining pages, or open the editor with what’s ready.
              </p>
            ) : null}
            {(job.status === 'failed' || jobStale) && projectId ? (
              <Button
                type="button"
                variant="outline"
                className="border-[#1f2635]"
                onClick={async () => {
                  try {
                    const { data } = await api.post(`/storybuilder/projects/${projectId}/retry`);
                    setJob({
                      id: data.job_id,
                      status: 'pending',
                      progress: 0,
                      message: 'Retry queued',
                    });
                    setHistoryRefreshKey((k) => k + 1);
                  } catch (err) {
                    toast.error(formatApiError(err, 'Retry failed'));
                  }
                }}
              >
                Retry pipeline
              </Button>
            ) : null}
            {canOpenEditor ? (
              <div className="flex flex-wrap gap-2 pt-1">
                <Button
                  type="button"
                  variant="outline"
                  className="border-[#1f2635]"
                  onClick={() => navigate(`/narration/${projectId}/edit`)}
                >
                  Open Scene View
                </Button>
                <Button
                  type="button"
                  className="bg-[#62FAE3] text-black hover:bg-[#4de1c1]"
                  onClick={() => navigate(`/narration/${projectId}/video-editor`)}
                >
                  Open Video Editor
                </Button>
              </div>
            ) : null}
          </div>
        ) : null}
      </div>

      <StorybuilderHistoryPanel
        open={historyOpen}
        onToggle={toggleHistory}
        activeProjectId={projectId}
        onSelectProject={selectHistoryProject}
        onNewProject={resetForNewProject}
        refreshKey={historyRefreshKey}
        loadingProjectId={loadingHistoryId}
      />

      <NarrationVoicePickerDialog
        open={voicePickerOpen}
        onOpenChange={setVoicePickerOpen}
        provider={voiceProvider}
        language={language}
        onSelectVoice={selectVoice}
      />
    </div>
  );
}

export default StoryBuilderFlowPage;
